import type { InterfaceToken } from '@morpho-org/dapp-core'

import { cn } from '@morpho-org/ui-v6/utils'
import { formatters, getDigitsFromPrice } from '@morpho-org/ui-v6/utils'
import { forwardRef, type HTMLAttributes } from 'react'

export interface CellRewardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Token object with decimals and price information */
  token?: InterfaceToken | null
  /** Token amount as bigint */
  amount?: bigint | null
  /** USD value of the amount (18 decimals) */
  amountUsd?: bigint | null
}

/**
 * CellReward component - Tailwind replica of dapp-ui-v5 CellReward
 *
 * Displays a token amount with its USD value in a vertical stack.
 *
 * @example
 * ```tsx
 * <CellReward
 *   token={token}
 *   amount={1000000000000000000n}
 *   amountUsd={1500000000000000000000n}
 * />
 * ```
 */
const CellReward = forwardRef<HTMLDivElement, CellRewardProps>(
  ({ token, amount, amountUsd, className, ...props }, ref) => {
    const formattedAmount = formatters.short
      .trailingZero(false)
      .default('-')
      .digits(getDigitsFromPrice(token?.price))
      .of(amount, token?.decimals)

    const formattedValueUsd = formatters.short
      .trailingZero(false)
      .unit('$')
      .digits(2)
      .of(amountUsd, 18)

    return (
      <div ref={ref} className={cn('flex flex-col items-start', className)} {...props}>
        <span className="text-2xs text-foreground">{formattedAmount}</span>
        <span className="text-2xs text-secondary-foreground">{formattedValueUsd}</span>
      </div>
    )
  }
)

CellReward.displayName = 'CellReward'

export { CellReward }
